/**
 * LenderLogo — single reusable component for showing a lender's logo
 * everywhere in the CRM.
 *
 * Always renders a container box. If a logo URL is provided AND the image
 * loads successfully, we show the image. Otherwise we render the initials
 * fallback (e.g. "PM" for Pepper Money) inside the same container.
 *
 * Sizes: 'sm' = 40x28, 'md' = 64x42 (default), 'lg' = 96x64
 * Compact: hides the border/background chrome — for use inside dense pills.
 */
import { useState } from "react"
import { cn } from "@/lib/utils"

export type LenderLogoSize = "sm" | "md" | "lg"

type Props = {
  name: string
  logoUrl?: string | null
  size?: LenderLogoSize
  compact?: boolean
  selected?: boolean
  className?: string
  /** Optional href — when set the logo is clickable (e.g. link to lender profile) */
  onClick?: () => void
}

const SIZE_MAP: Record<
  LenderLogoSize,
  { box: string; image: string; text: string }
> = {
  sm: { box: "h-7 w-10", image: "max-h-5 max-w-8", text: "text-[10px]" },
  md: { box: "h-[42px] w-16", image: "max-h-[30px] max-w-[52px]", text: "text-xs" },
  lg: { box: "h-16 w-24", image: "max-h-12 max-w-20", text: "text-base" },
}

/** Build initials like "Pepper Money" -> "PM", "Bank of Queensland" -> "BQ". */
export function lenderInitials(name: string): string {
  const words = name
    .replace(/[.&,]/g, " ")
    .split(/\s+/)
    .filter(Boolean)
    .filter((w) => !["of", "the", "and", "&"].includes(w.toLowerCase()))
  if (words.length === 0) return "?"
  if (words.length === 1) {
    const w = words[0]!
    return w.length >= 2 ? (w[0]! + w[1]!).toUpperCase() : w[0]!.toUpperCase()
  }
  return (words[0]![0]! + words[1]![0]!).toUpperCase()
}

/** Deterministic pastel background per lender for the fallback badge. */
function fallbackBg(name: string): string {
  const palette = [
    "bg-blue-100 text-blue-800",
    "bg-emerald-100 text-emerald-800",
    "bg-amber-100 text-amber-800",
    "bg-violet-100 text-violet-800",
    "bg-cyan-100 text-cyan-800",
    "bg-rose-100 text-rose-800",
    "bg-indigo-100 text-indigo-800",
    "bg-teal-100 text-teal-800",
  ]
  let hash = 0
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) | 0
  return palette[Math.abs(hash) % palette.length]!
}

export function LenderLogo({
  name,
  logoUrl,
  size = "md",
  compact = false,
  selected = false,
  className,
  onClick,
}: Props) {
  const [failed, setFailed] = useState(false)
  const sz = SIZE_MAP[size]
  const showImage = !!logoUrl && !failed

  const containerCls = cn(
    "grid place-items-center shrink-0 overflow-hidden",
    sz.box,
    compact
      ? "rounded"
      : "rounded-lg bg-white border p-1",
    compact
      ? ""
      : selected
        ? "border-rc-blue-500 ring-2 ring-rc-blue-100"
        : "border-slate-200",
    onClick && "cursor-pointer hover:border-rc-blue-300 transition-colors",
    className
  )

  const Wrapper: "button" | "div" = onClick ? "button" : "div"

  return (
    <Wrapper
      type={onClick ? "button" : undefined}
      onClick={onClick}
      className={containerCls}
      aria-label={`${name} logo`}
    >
      {showImage ? (
        <img
          src={logoUrl!}
          alt={name}
          loading="lazy"
          onError={() => setFailed(true)}
          className={cn("object-contain", sz.image)}
          style={{ objectFit: "contain" }}
        />
      ) : (
        <span
          className={cn(
            "grid place-items-center rounded font-bold leading-none",
            sz.text,
            fallbackBg(name),
            "h-full w-full"
          )}
        >
          {lenderInitials(name)}
        </span>
      )}
    </Wrapper>
  )
}
